// Composes the 9 export actions available from the reporting top-nav
// Export button. Each key maps to a download handler; `run(keys)` fires the
// selected ones in parallel and reports success/failure per action via
// toast. The `session_pdf` action flips a Redux flag that
// <SessionWholeExport/> watches and turns into a window.print() call.
import { getEventById } from '@frontend/core/lib/domains/events'
import { startWholeSessionPrint } from '@frontend/core/lib/domains/reporting'
import type { SessionDownloadOption } from '@frontend/core/lib/domains/sessions/download-definitions'
import {
  SESSION_DOWNLOAD_DEFINITIONS,
  SESSION_DOWNLOAD_OPTIONS,
  sortByDisplayPreference
} from '@frontend/core/lib/domains/sessions/download-definitions'
import {
  downloadEventsCSV,
  downloadEventsJSON,
  downloadPagePdf,
  downloadPhysicalMetricsCSV,
  downloadPitchAnchorZip,
  downloadPlayerMetricsCSV,
  downloadPositionPacketsParquet,
  downloadSessionMetadataCSV,
  downloadSessionPlayersJSON,
  downloadSportscode
} from '@frontend/core/lib/domains/sessions/downloads'
import { selectServerUrl } from '@frontend/core/lib/modules/servers/selectors'
import {
  useAppDispatch,
  useAppSelector,
  useSelectedOrganisationId
} from '@frontend/react'
import { toast } from '@frontend/ui'
import { useCallback } from 'react'

// Preserves the dropdown's existing display order — independent of
// `SESSION_DOWNLOAD_OPTIONS`' own canonical ordering — while sourcing
// membership from the shared definitions, so an option gaining or losing
// `'reporting'` from its `apps` list is reflected automatically. An option
// not listed here (e.g. a brand-new one added later) still appears, just
// ordered after every listed preference rather than being silently dropped.
const EXPORT_DISPLAY_PREFERENCE: SessionDownloadOption[] = [
  'page_pdf',
  'session_pdf',
  'events_json',
  'events_csv',
  'pitch_anchor_json',
  'session_players_json',
  'session_metadata_csv',
  'physical_metrics_csv',
  'position_packets_parquet',
  'sportscode_xml'
]

export const EXPORT_OPTIONS = sortByDisplayPreference(
  SESSION_DOWNLOAD_OPTIONS.filter((option) =>
    SESSION_DOWNLOAD_DEFINITIONS[option].apps.includes('reporting')
  ),
  EXPORT_DISPLAY_PREFERENCE
)

// Figma labels — mostly copied verbatim from node 2225:86670. `sportscode_xml`
// was added after the handoff to expose the broadcast-API `/eventsSportsCode`
// endpoint (Hudl Sportscode XML timeline).
export const EXPORT_LABELS: Record<SessionDownloadOption, string> = {
  page_pdf: 'Export page (pdf)',
  session_pdf: 'Entire session (pdf)',
  events_json: 'Events (json)',
  events_csv: 'Events (csv)',
  pitch_anchor_json: 'Pitch & anchor data (json)',
  session_players_json: 'Session players (json)',
  player_metrics_csv: 'Player metrics (csv)',
  session_metadata_csv: 'Session metadata (csv)',
  physical_metrics_csv: 'Physical metrics (csv)',
  position_packets_parquet: 'Position packets (parquet)',
  sportscode_xml: 'Sportscode (xml)'
}

interface UseSessionExportArgs {
  sessionId: string
  sessionDate: string
  sessionName: string
}

export function useSessionExport({
  sessionId,
  sessionDate,
  sessionName
}: UseSessionExportArgs) {
  const dispatch = useAppDispatch()
  const broadcastBaseUrl = useAppSelector(selectServerUrl('broadcastApi'))
  const organisationId = useSelectedOrganisationId()

  const runOne = useCallback(
    async (option: SessionDownloadOption, unit?: string): Promise<void> => {
      // All broadcast-API helpers need the resolved base URL. Fail fast with
      // a clear message when servers haven't been configured yet.
      const requireBaseUrl = (): string => {
        if (!broadcastBaseUrl) {
          throw new Error('Broadcast server URL is not configured')
        }

        return broadcastBaseUrl
      }
      const handlers: Record<SessionDownloadOption, () => Promise<void>> = {
        page_pdf: async () => {
          // window.print() is synchronous — don't emit a success toast here
          // because it would show up in the print preview.
          downloadPagePdf()
        },
        session_pdf: async () => {
          // <SessionWholeExport/> takes it from here: mounts all 3 print
          // layouts and calls window.print() once they're in the DOM.
          dispatch(startWholeSessionPrint())
        },
        events_csv: () =>
          downloadEventsCSV(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            {
              unit,
              orgId: organisationId ?? undefined
            }
          ),
        events_json: () =>
          downloadEventsJSON(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            {
              unit,
              orgId: organisationId ?? undefined
            }
          ),
        player_metrics_csv: () =>
          downloadPlayerMetricsCSV(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            { unit, orgId: organisationId ?? undefined }
          ),
        session_metadata_csv: () =>
          downloadSessionMetadataCSV(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            { unit, orgId: organisationId ?? undefined }
          ),
        physical_metrics_csv: () =>
          downloadPhysicalMetricsCSV(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            { unit, orgId: organisationId ?? undefined }
          ),
        position_packets_parquet: () =>
          downloadPositionPacketsParquet(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            { orgId: organisationId ?? undefined }
          ),
        sportscode_xml: () =>
          downloadSportscode(
            requireBaseUrl(),
            sessionId,
            sessionDate,
            sessionName,
            {
              unit,
              orgId: organisationId ?? undefined
            }
          ),
        pitch_anchor_json: async () => {
          const event = await dispatch(
            getEventById.initiate({
              id: sessionId,
              orgId: organisationId ?? undefined
            })
          ).unwrap()
          if (event.dataType !== 'Session') {
            throw new Error('Event is not a session')
          }
          const pitch = event.data?.pitch
          if (!pitch) throw new Error('Pitch not found on session')
          await downloadPitchAnchorZip(pitch, sessionName)
        },
        session_players_json: async () => {
          const event = await dispatch(
            getEventById.initiate({
              id: sessionId,
              orgId: organisationId ?? undefined
            })
          ).unwrap()
          if (event.dataType !== 'Session') {
            throw new Error('Event is not a session')
          }
          downloadSessionPlayersJSON(
            event.data.players,
            sessionDate,
            sessionName
          )
        }
      }
      await handlers[option]()
    },
    [
      dispatch,
      broadcastBaseUrl,
      sessionId,
      sessionDate,
      sessionName,
      organisationId
    ]
  )

  const run = useCallback(
    async (options: SessionDownloadOption[], unit?: string): Promise<void> => {
      const results = await Promise.allSettled(
        options.map((o) => runOne(o, unit))
      )
      results.forEach((result, i) => {
        const option = options[i]
        const label = EXPORT_LABELS[option]
        if (result.status === 'fulfilled') {
          // No success toast for the two synchronous print actions — the
          // print preview would swallow it.
          if (option !== 'page_pdf' && option !== 'session_pdf') {
            toast.success(`${label} downloaded`)
          }
        } else {
          const message =
            result.reason instanceof Error
              ? result.reason.message
              : `Failed to export ${label}`
          toast.error(message)
        }
      })
    },
    [runOne]
  )

  return { run }
}
